import Close from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import { IconButton, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { EditableField } from "../edit-content/editable-field";
import { useUpdateAssetCallback } from "./hooks/use-upsert-asset";
import { LinkToAssetButton } from "./link-to-asset-button";

import type { Asset } from "./server/digital-asset-service";
import type { FC } from "react";

export const AssetPreviewModalTitle: FC<{
  asset: Asset;
  filename: string;
  setFilename: (value: string) => void;
  setFilenameUpdated: (value: boolean) => void;
  handleCloseDialog: () => void;
}> = ({ asset, filename, setFilename, setFilenameUpdated, handleCloseDialog }) => {
  const [editable, setEditable] = useState<boolean>(false);
  const updateAsset = useUpdateAssetCallback();

  const handleSetFilename = useCallback(
    (newFileName: string) => {
      setFilename(newFileName);
    },
    [setFilename],
  );

  const updateAssetTitle = useCallback(
    async (newTitle: string) => {
      const { success, asset: savedAsset, error } = await updateAsset({ asset, title: newTitle });

      if (!success || !savedAsset) {
        if (error?.key === "name") {
          setFilename(asset.title);
          toast.error(error.message);

          return;
        }
      }

      toast.success(`Filename updated to "${newTitle}"`);
      setFilenameUpdated(true);
    },
    [asset, setFilename, setFilenameUpdated, updateAsset],
  );

  return (
    <Stack spacing={0} sx={{ width: "100%" }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <EditableField
            title={filename}
            editable={editable}
            setEditable={setEditable}
            setFilename={handleSetFilename}
            onChange={(newTitle: string) =>
              newTitle !== asset.title && void updateAssetTitle(newTitle)
            }
            variant="h6"
          />
          <IconButton
            data-analytics-id="edit-content-edit-name"
            size="small"
            data-testid="edit-content-edit-name-button"
            onClick={() => {
              setEditable(true);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Stack>
        <IconButton
          onClick={handleCloseDialog}
          data-analytics-id="content-library-preview-close"
          title="Close"
          size="small"
        >
          <Close data-testid="CloseIcon" />
        </IconButton>
      </Stack>
      <LinkToAssetButton asset={asset} />
    </Stack>
  );
};
